/**
 * Record marks (task 10; the owner: "should have the option to add a note and say This task was done incorrectly. Also
 * optional photo snap proof."). Three things a person can add to a record in Today's records. A mark is ADDED, never
 * edited or removed, and the record underneath is never changed (a fix is a new record; corrections.ts still owns
 * "not done after all"):
 *
 * | M1 Note | anyone who can see the record may add a short note; append-only |
 * | M2 Done wrong | only a manager, owner or the person in charge, never on their own record; a reason is required; flags the record and sends the recorder a private "Please look again" (teamwork.ts fixAskLine, the ask-to-fix path); it says the record needs another look, it doesn't accuse |
 * | M3 Photo | anyone who can see the record may add a photo as proof; append-only |
 * | M4 Private stays private | a check-in or a sick report takes no mark at all: no note, no flag, no photo (todayrecords.ts D2, T4) |
 * | M5 Visible where it counts | the marks show under the record, and in the binder's "Notes, flags and photos" section |
 */
import type { Id, IsoDateTime } from "./model.ts";
import type { Collection } from "./repo.ts";

export type MarkKind = "note" | "wrong" | "photo";
export const MARK_TEXT_MAX = 300;

export interface RecordMark {
  id: Id;
  orgId: Id;
  locationId: Id;
  /** The Today row it is on: "<kind>:<id>". */
  recordRef: string;
  /** The record's title and time when it was marked, so the binder can say which one without loading it. */
  recordTitle: string;
  recordAt: IsoDateTime;
  kind: MarkKind;
  /** The note, or the reason it was done wrong. */
  text?: string;
  /** Stored photo path (storage key). */
  photoPath?: string;
  by: Id;
  at: IsoDateTime;
}
export type RecordMarkRepo = Collection<RecordMark>;

/** What the person sees under a record. `byName` is looked up when read (personname.ts N1), never stored. */
export interface RecordMarkView { id: Id; kind: MarkKind; text?: string; photoPath?: string; byName: string; time: string; at: IsoDateTime }
export interface RecordCan { note: boolean; wrong: boolean; photo: boolean }

/** M1-M4: what this person may add to this row. */
export function recordCan(row: { kind: string; mine: boolean; sick?: boolean }, viewer: { manager: boolean; inCharge?: boolean }): RecordCan {
  if (row.kind === "checkin" || row.sick) return { note: false, wrong: false, photo: false };
  return { note: true, photo: true, wrong: (viewer.manager || !!viewer.inCharge) && !row.mine };
}

/** Why this can't be added, or null. `hasPhoto` is a decoded photo in hand. */
export function markProblem(kind: unknown, o: { text?: unknown; hasPhoto: boolean }): string | null {
  if (kind !== "note" && kind !== "wrong" && kind !== "photo") return '"kind" must be note, wrong or photo.';
  if (o.text !== undefined && o.text !== null && typeof o.text !== "string") return "The words must be text.";
  const text = typeof o.text === "string" ? o.text.trim() : "";
  if (text.length > MARK_TEXT_MAX) return `Keep it to ${MARK_TEXT_MAX} letters.`;
  if (kind === "note" && !text) return "Write your note first.";
  if (kind === "wrong" && !text) return "Say what was wrong, so they know what to look at again.";
  if (kind === "photo" && !o.hasPhoto) return "Take or choose a photo first.";
  return null;
}

export const MARK_WORD: Record<MarkKind, string> = { note: "Note", wrong: "Done wrong", photo: "Photo" };

export interface MarkRefusal { code: "invalid" | "forbidden" | "not_found"; message: string }

/**
 * The one decision, shared by the API and both demo mocks: may this viewer add this to this row? `rows` are the rows AS
 * THAT VIEWER SEES THEM (todaysRecords), so a row they can't see doesn't exist for them (D1) and `row.can` carries M1-M4.
 */
export function markAttempt<R extends { id: string; kind: string; mine: boolean; can: RecordCan }>(rows: readonly R[], a: { recordRef: unknown; kind: unknown; text?: unknown; hasPhoto: boolean }): MarkRefusal | { ok: true; row: R; kind: MarkKind; text?: string } {
  const bad = markProblem(a.kind, { text: a.text, hasPhoto: a.hasPhoto });
  if (bad) return { code: "invalid", message: bad };
  const kind = a.kind as MarkKind;
  const row = rows.find((r) => r.id === a.recordRef);
  if (!row) return { code: "not_found", message: "That record isn't in today's records." };
  if (!row.can[kind]) {
    if (row.kind === "checkin" || !row.can.note) return { code: "forbidden", message: "A health check-in or a sick report can't be marked. It stays private." };
    if (kind === "wrong") return { code: "forbidden", message: row.mine ? "You can't mark your own record. Ask a manager to look at it." : "Only a manager, an owner or the person in charge can say a record was done wrong." };
    return { code: "forbidden", message: "You can't add to this record." };
  }
  const text = typeof a.text === "string" ? a.text.trim() : "";
  return { ok: true, row, kind, ...(text ? { text } : {}) };
}

/** The binder section's rows: which record, what was added, by whom, when. Photos are on file, not drawn. */
export function markRows(marks: readonly RecordMark[], names: Readonly<Record<string, string>>, timeLabel: (iso: string) => string, shortOf: (full?: string | null) => string): string[][] {
  return [...marks].sort((a, b) => a.at.localeCompare(b.at)).map((m) => [
    timeLabel(m.recordAt), m.recordTitle, MARK_WORD[m.kind],
    m.kind === "photo" ? `Photo on file${m.text ? `: ${m.text}` : ""}` : m.text ?? "", shortOf(names[m.by]) || "", timeLabel(m.at),
  ]);
}
